import { z } from "zod";
import { Cik, Timestamp } from "./primitives.js";
/** A ticker recorded for a company, with whether it is the current one. */
export const CompanyTicker = z.object({
    ticker: z.string(),
    exchange: z.string().nullable(),
    isCurrent: z.boolean(),
});
/** An EDGAR registrant, as `GET /api/companies/:cik` returns it. */
export const Company = z.object({
    cik: Cik,
    name: z.string(),
    sic: z.string().nullable(),
    sicDescription: z.string().nullable(),
    stateOfIncorporation: z.string().nullable(),
    ein: z.string().nullable(),
    /** Prior names with the window each was in use — a spin-off often renames the parent. */
    formerNames: z.array(z.object({ name: z.string(), from: z.string(), to: z.string() })),
    tickers: z.array(CompanyTicker),
    firstSeen: Timestamp.nullable(),
    lastSeen: Timestamp.nullable(),
});
/** Minimal company shape for embedding in a situation row without a second query. */
export const CompanyRef = z.object({
    cik: Cik.nullable(),
    name: z.string(),
    ticker: z.string().nullable(),
});
/**
 * EDGAR's trailing qualifier, which a filing's prose never carries.
 *
 * Usually a state of incorporation, but not always: `APPLIED MATERIALS INC /DE`,
 * `BANK OF AMERICA CORP /DE/`, `COSTCO WHOLESALE CORP /NEW`, `RESOLUTION MINERALS LTD/ADR`.
 */
export const EDGAR_NAME_QUALIFIER = /\s*\/[A-Za-z]{2,4}\/?$/;
/**
 * The corporate suffix, mapped from the form prose uses to the form EDGAR stores.
 *
 * Only suffixes EDGAR abbreviates belong here. `holdings`, `group`, `trust` and `fund`
 * are absent because EDGAR writes them in full, so mapping them would invent a
 * difference rather than close one.
 *
 * The one vocabulary shared by canonicalising a name for lookup and trimming one for
 * display.
 */
export const SUFFIX_TO_EDGAR = {
    corporation: "corp",
    incorporated: "inc",
    company: "co",
    limited: "ltd",
};
/**
 * Every spelling of a corporate suffix worth dropping from a name shown on screen.
 *
 * Foreign forms (`AG`, `SA`, `N.V.`) are deliberately left in: stripping them means
 * handling their punctuated spellings.
 */
export const CORPORATE_SUFFIXES = new Set([
    ...Object.keys(SUFFIX_TO_EDGAR),
    ...Object.values(SUFFIX_TO_EDGAR),
    "plc",
    "lp",
    "llc",
]);
/**
 * A company name with the parts that carry no information removed, for a dense view.
 *
 * Removes EDGAR's trailing qualifier, every trailing corporate suffix (`Foo Co Ltd` loses
 * both) and a connector left dangling (`Tiffany & Co` becomes `Tiffany`). Otherwise the
 * name is kept exactly as stored; unlike `normalizeCompanyName`, it does not lowercase or
 * strip punctuation.
 *
 * @param name the stored company name
 * @returns the trimmed name, or the name untouched (bar surrounding whitespace) when it
 *   is entirely suffix
 */
export function displayCompanyName(name) {
    const withoutQualifier = name.trim().replace(EDGAR_NAME_QUALIFIER, "");
    let words = withoutQualifier.split(/\s+/).filter(Boolean);
    for (;;) {
        const last = words[words.length - 1];
        if (last === undefined)
            break;
        // Punctuation rides along with the token: `Inc.` and `Inc` are the same suffix, and
        // EDGAR is inconsistent about the comma before it.
        const bare = last.replace(/[^A-Za-z]/g, "").toLowerCase();
        if (!CORPORATE_SUFFIXES.has(bare))
            break;
        words = words.slice(0, -1);
        // The comma that introduced the suffix — "Resolute Holdings Management, Inc."
        const previous = words[words.length - 1];
        if (previous !== undefined)
            words[words.length - 1] = previous.replace(/,$/, "");
    }
    while (words.length > 0 && /^(&|and)$/i.test(words[words.length - 1]))
        words = words.slice(0, -1);
    return words.length > 0 ? words.join(" ") : name.trim();
}
//# sourceMappingURL=company.js.map